/** Cross-cutting business constants. Anything a magic number would otherwise be. */

export const APP_NAME = 'ShreeHari Solar ERP';
export const DEFAULT_CURRENCY = 'INR';
export const DEFAULT_LOCALE = 'en-IN';
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/** Indian FY runs April–March. */
export const FISCAL_YEAR_START_MONTH = 4;

/* ------------------------------------------------------------------ */
/* Listing defaults                                                    */
/* ------------------------------------------------------------------ */

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** Hard ceiling on a single Excel export, to keep the request synchronous. */
export const MAX_EXPORT_ROWS = 20_000;

/* ------------------------------------------------------------------ */
/* Uploads                                                             */
/* ------------------------------------------------------------------ */

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const MAX_UPLOAD_FILES = 10;

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ACCEPTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
] as const;

/* ------------------------------------------------------------------ */
/* Document numbering                                                  */
/* ------------------------------------------------------------------ */

/**
 * Prefixes for human-readable document numbers. Final format is
 * `<PREFIX>/<FY>/<0001>` — e.g. `QTN/25-26/0042`. Sequences are allocated
 * atomically per (prefix, fiscal year) by the API's numbering service.
 */
export const DOC_PREFIX = {
  LEAD: 'LD',
  CUSTOMER: 'CUS',
  SURVEY: 'SRV',
  QUOTATION: 'QTN',
  WORK_ORDER: 'WO',
  PROJECT: 'PRJ',
  DISCOM: 'DSC',
  SYSTEM_DESIGN: 'DSN',
  CUSTOMER_APPROVAL: 'APR',
  MATERIAL_PLAN: 'MRP',
  FABRICATION: 'FAB',
  MATERIAL_DISPATCH: 'MDN',
  INSTALLATION: 'INS',
  ELECTRICAL_TEST: 'TST',
  NET_METER: 'NMA',
  DISCOM_INSPECTION: 'DIN',
  PROJECT_CLOSURE: 'CLS',
  SERVICE_TICKET: 'SRT',
  AMC_CONTRACT: 'AMC',
  COMMISSIONING: 'COM',
  SUBSIDY: 'SUB',
  VENDOR: 'VND',
  PURCHASE_REQUISITION: 'PR',
  PURCHASE_ORDER: 'PO',
  GOODS_RECEIPT: 'GRN',
  PURCHASE_RETURN: 'PRT',
  PROFORMA_INVOICE: 'PI',
  /// The GST tax invoice. A separate series from PI on purpose — a proforma
  /// carries no tax liability and a tax invoice does, and the two must never
  /// share a running number.
  SALES_INVOICE: 'INV',
  CREDIT_NOTE: 'CRN',
  DISPATCH: 'DSP',
  STOCK_ALLOCATION: 'ALC',
  STOCK_TRANSFER: 'STF',
  STOCK_ADJUSTMENT: 'ADJ',
  DEALER: 'DLR',
  TENDER: 'TND',
  EXPENSE: 'EXP',
  PAYMENT: 'PAY',
  /// An online collection request. A separate series from PAY on purpose: most
  /// gateway orders never become payments, because customers abandon checkouts,
  /// and letting them consume receipt numbers would leave permanent holes in
  /// the receipt series that an auditor is entitled to ask about.
  PAYMENT_ORDER: 'PGO',
  REFERRAL: 'REF',
  PAYOUT: 'PYT',

  // ---- HR ----------------------------------------------------------
  EMPLOYEE: 'EMP',
  LEAVE_REQUEST: 'LV',
  EMPLOYEE_TASK: 'TSK',
  PERFORMANCE_REVIEW: 'PRF',
} as const;

export type DocPrefixKey = keyof typeof DOC_PREFIX;

/* ------------------------------------------------------------------ */
/* Domain rules                                                        */
/* ------------------------------------------------------------------ */

/** GST slabs applicable to solar goods and EPC services in India. */
export const GST_RATES = [0, 5, 12, 18, 28] as const;

/** PM Surya Ghar residential subsidy slabs (₹ per sanctioned capacity). */
export const PM_SURYA_GHAR_SLABS = [
  { upToKw: 1, amountInr: 30_000 },
  { upToKw: 2, amountInr: 60_000 },
  { upToKw: 3, amountInr: 78_000 },
  { upToKw: Number.POSITIVE_INFINITY, amountInr: 78_000 },
] as const;

/**
 * The GST rate a new quotation line starts at, by proposal group.
 *
 * Goods that make up a solar power generating system sit at 12% and the labour
 * to install it at 18% — the split most Indian rooftop EPC houses quote on since
 * the October 2021 revision. These are *defaults for a blank line*, not a ruling:
 * every line carries its own editable rate, because the correct treatment depends
 * on how the contract is structured and that is the seller's call, not ours.
 */
export const QUOTATION_CATEGORY_GST_PCT = {
  SOLAR_PANEL: 12,
  INVERTER: 12,
  STRUCTURE: 12,
  CABLE: 12,
  PROTECTION: 12,
  INSTALLATION: 18,
  OTHER_SERVICE: 18,
  OTHER: 18,
} as const;

/**
 * The composite-supply split.
 *
 * Where a rooftop system is quoted as one turnkey price rather than as separate
 * goods and services, the established practice is to treat 70% of the contract
 * value as goods at 12% and 30% as service at 18% — an effective 13.8%. The form
 * offers this as a one-click way to turn a single agreed price into the two lines
 * that carry it; it is not applied automatically to anything.
 */
export const COMPOSITE_SUPPLY_SPLIT = {
  goodsSharePct: 70,
  goodsGstPct: 12,
  serviceSharePct: 30,
  serviceGstPct: 18,
} as const;

/** Default quotation validity in days. */
export const QUOTATION_VALIDITY_DAYS = 15;

/** Payment follow-up is flagged overdue this many days past the due date. */
export const FOLLOW_UP_GRACE_DAYS = 3;

/* ------------------------------------------------------------------ */
/* Project pipeline                                                    */
/* ------------------------------------------------------------------ */

/**
 * When a running project stops being "fine".
 *
 * Health is a reading, not a stored field: it is recomputed from the clock on
 * every request, because a project that was on track yesterday is delayed today
 * without anybody having touched it. Storing it would mean a nightly job whose
 * failure mode is a dashboard that is quietly wrong.
 *
 * The two ratios below are applied to the *stage's* own SLA, so a 30-day DISCOM
 * wait and a 7-day installation are each judged against what they should take
 * rather than against one global number.
 */
export const PROJECT_HEALTH_RULES = {
  /** Time in the current stage, as a multiple of that stage's SLA. */
  atRiskStageSlaRatio: 1,
  delayedStageSlaRatio: 2,
  /**
   * How close the target completion date has to be, with the work not
   * correspondingly far along, before the project is flagged.
   */
  atRiskCompletionWindowDays: 7,
  /** Progress expected by the time the completion date is that close. */
  atRiskProgressPct: 80,
} as const;

/**
 * Stage codes whose entry opens a workfile in another module.
 *
 * Reaching INSTALLATION creates the installation record, reaching COMMISSIONING
 * creates the commissioning record, and so on — the stage transition provisions
 * the thing the next team works in. The alternative is a guard that refuses to
 * advance until somebody has manually created an empty record, which is a step
 * that exists only to satisfy the guard.
 */
export const STAGE_PROVISIONS = {
  MATERIAL_PLANNING: 'materialPlan',
  FABRICATION: 'fabrication',
  INSTALLATION: 'installation',
  ELECTRICAL_TESTING: 'electricalTest',
  NET_METERING: 'netMeterApplication',
  DISCOM_INSPECTION: 'discomInspection',
  COMMISSIONING: 'commissioning',
  PROJECT_CLOSURE: 'projectClosure',
} as const;

export const REGEX = {
  /**
   * Deliberately permissive — the authoritative check on an address is whether a
   * mail sent to it arrives. This only has to separate "looks like an email" from
   * "looks like a phone number" at sign-in, and reject obvious typos in a form.
   */
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  GSTIN: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  PAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  AADHAAR: /^[2-9][0-9]{11}$/,
  IFSC: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  PINCODE: /^[1-9][0-9]{5}$/,
  PHONE_IN: /^[6-9][0-9]{9}$/,
} as const;

/* ------------------------------------------------------------------ */
/* EPC execution workflow                                              */
/* ------------------------------------------------------------------ */

/**
 * The nine electrical tests, with the thresholds an Indian rooftop system is
 * signed off against.
 *
 * These are the *defaults a new test sheet is built from*, not a standard: every
 * reading carries its own editable threshold, because the acceptable insulation
 * resistance depends on the string length and the acceptable earth resistance on
 * the soil. What is fixed is the list — a sheet that silently omitted the
 * polarity test would look complete.
 */
export const ELECTRICAL_TEST_TEMPLATE = [
  {
    testType: 'INSULATION_RESISTANCE',
    label: 'DC string insulation resistance',
    unit: 'MΩ',
    expectedValue: '≥ 1',
    minThreshold: 1,
    isMandatory: true,
  },
  {
    testType: 'EARTH_RESISTANCE',
    label: 'Earth pit resistance',
    unit: 'Ω',
    expectedValue: '≤ 5',
    maxThreshold: 5,
    isMandatory: true,
  },
  {
    testType: 'VOLTAGE',
    label: 'String open-circuit voltage (Voc)',
    unit: 'V',
    expectedValue: 'Within 5% of design',
    isMandatory: true,
  },
  {
    testType: 'CURRENT',
    label: 'String short-circuit current (Isc)',
    unit: 'A',
    expectedValue: 'Within 10% of design',
    isMandatory: true,
  },
  {
    testType: 'POLARITY',
    label: 'DC polarity at combiner and inverter',
    unit: null,
    expectedValue: 'Correct',
    isMandatory: true,
  },
  {
    testType: 'CONTINUITY',
    label: 'Earth continuity, module frame to earth bus',
    unit: 'Ω',
    expectedValue: '< 1',
    maxThreshold: 1,
    isMandatory: true,
  },
  {
    testType: 'INVERTER',
    label: 'Inverter start-up, grid sync and display',
    unit: null,
    expectedValue: 'Sync achieved',
    isMandatory: true,
  },
  {
    testType: 'PROTECTION',
    label: 'Anti-islanding, over/under voltage and frequency trip',
    unit: null,
    expectedValue: 'Trips within limits',
    isMandatory: true,
  },
  {
    testType: 'GENERATION',
    label: 'AC power output against irradiance',
    unit: 'kW',
    expectedValue: 'Within 10% of expected',
    isMandatory: false,
  },
] as const;

/**
 * The installation QC checklist, raised with every installation.
 *
 * Seeded onto the record at creation rather than evaluated at read time, unlike
 * the closure checklist: nothing in the database can prove that a cable was
 * clipped at the right spacing or that the array was torqued to spec. These are
 * assertions a named person makes on site, and the value of the record is the
 * name and the timestamp against each one.
 *
 * `mandatory` items block completion. They are the ones that are dangerous or
 * expensive to discover later — earthing, polarity, structure fixing, isolation
 * — as opposed to the ones that are merely untidy.
 */
export const INSTALLATION_CHECKLIST = [
  { code: 'STRUCTURE_ANCHORED', label: 'Structure anchored and grouted to specification', category: 'Structure', mandatory: true },
  { code: 'STRUCTURE_TORQUE', label: 'Fasteners torqued and marked', category: 'Structure', mandatory: true },
  { code: 'MODULE_MOUNTED', label: 'Modules mounted with specified clamping and gaps', category: 'Structure', mandatory: true },
  { code: 'ROOF_WATERPROOFING', label: 'Roof penetrations sealed and waterproofed', category: 'Structure', mandatory: true },
  { code: 'DC_CABLE_ROUTED', label: 'DC cabling routed, clipped and UV-protected', category: 'Electrical', mandatory: true },
  { code: 'DC_POLARITY', label: 'String polarity verified before connection', category: 'Electrical', mandatory: true },
  { code: 'MC4_CRIMP', label: 'MC4 connectors crimped and seated', category: 'Electrical', mandatory: true },
  { code: 'DCDB_INSTALLED', label: 'DCDB mounted with fuses and SPD', category: 'Electrical', mandatory: true },
  { code: 'ACDB_INSTALLED', label: 'ACDB mounted with breaker rating as designed', category: 'Electrical', mandatory: true },
  { code: 'INVERTER_MOUNTED', label: 'Inverter mounted with clearance and ventilation', category: 'Electrical', mandatory: true },
  { code: 'AC_CABLE_ROUTED', label: 'AC cabling to the metering point completed', category: 'Electrical', mandatory: true },
  { code: 'EARTHING_PITS', label: 'Earthing pits dug, filled and continuity verified', category: 'Safety', mandatory: true },
  { code: 'LIGHTNING_ARRESTER', label: 'Lightning arrester installed and earthed', category: 'Safety', mandatory: true },
  { code: 'ISOLATION_LABELS', label: 'Isolation points labelled and safety signage fitted', category: 'Safety', mandatory: true },
  { code: 'MONITORING_ONLINE', label: 'Inverter monitoring configured and reporting', category: 'Commissioning', mandatory: false },
  { code: 'SITE_CLEARED', label: 'Site cleared of packaging, offcuts and scrap', category: 'Handover', mandatory: false },
  { code: 'PHOTOS_TAKEN', label: 'Before, during and after photographs uploaded', category: 'Handover', mandatory: false },
] as const;

/**
 * The project closure checklist.
 *
 * `auto: true` lines are proved by the system on every read and cannot be ticked
 * by hand — this is what stops a project being closed by asserting a payment
 * that is not in the ledger. The rest are confirmations a person makes, because
 * no table records whether the customer was actually shown how to read the
 * inverter display.
 */
export const PROJECT_CLOSURE_CHECKLIST = [
  { code: 'QUOTATION', label: 'Quotation issued and accepted', category: 'Commercial', auto: true, mandatory: true },
  { code: 'CUSTOMER_APPROVAL', label: 'Customer approval on record', category: 'Commercial', auto: true, mandatory: true },
  { code: 'ADVANCE_PAYMENT', label: 'Advance payment received', category: 'Commercial', auto: true, mandatory: true },
  { code: 'MATERIAL_PLANNING', label: 'Material plan approved', category: 'Supply', auto: true, mandatory: true },
  { code: 'PROCUREMENT', label: 'Procurement closed', category: 'Supply', auto: false, mandatory: false },
  { code: 'FABRICATION', label: 'Fabrication completed or not required', category: 'Supply', auto: true, mandatory: true },
  { code: 'DISPATCH', label: 'Material dispatched and received on site', category: 'Supply', auto: true, mandatory: true },
  { code: 'INSTALLATION', label: 'Installation completed and QC passed', category: 'Execution', auto: true, mandatory: true },
  { code: 'ELECTRICAL_TESTING', label: 'Electrical testing passed', category: 'Execution', auto: true, mandatory: true },
  { code: 'NET_METERING', label: 'Net meter installed', category: 'Utility', auto: true, mandatory: true },
  { code: 'DISCOM', label: 'DISCOM inspection passed', category: 'Utility', auto: true, mandatory: true },
  { code: 'COMMISSIONING', label: 'System commissioned', category: 'Execution', auto: true, mandatory: true },
  { code: 'SUBSIDY', label: 'Subsidy claim settled or not applicable', category: 'Commercial', auto: true, mandatory: false },
  { code: 'FINAL_PAYMENT', label: 'Final payment received in full', category: 'Commercial', auto: true, mandatory: true },
  { code: 'WARRANTY_DOCUMENTS', label: 'Warranty records raised and handed over', category: 'Handover', auto: true, mandatory: true },
  { code: 'HANDOVER_DOCUMENTS', label: 'Handover pack signed by the customer', category: 'Handover', auto: false, mandatory: true },
  { code: 'CUSTOMER_TRAINING', label: 'Customer shown operation, monitoring and safety shutdown', category: 'Handover', auto: false, mandatory: true },
  { code: 'SITE_CLEARED', label: 'Site cleared of packaging and scrap', category: 'Handover', auto: false, mandatory: false },
] as const;

/**
 * Default warranty terms raised at closure, in years.
 *
 * Indian rooftop practice: the module carries a product warranty and a much
 * longer performance warranty from the manufacturer, the inverter a shorter one
 * from a different manufacturer, and the workmanship from the EPC contractor.
 * Editable per project — a tender frequently dictates its own terms.
 */
export const DEFAULT_WARRANTY_TERMS = [
  { component: 'SOLAR_PANEL', label: 'Module product warranty', termYears: 12 },
  { component: 'PANEL_PERFORMANCE', label: 'Module performance warranty', termYears: 25 },
  { component: 'INVERTER', label: 'Inverter warranty', termYears: 5 },
  { component: 'MOUNTING_STRUCTURE', label: 'Mounting structure', termYears: 10 },
  { component: 'BALANCE_OF_SYSTEM', label: 'Balance of system', termYears: 2 },
  { component: 'WORKMANSHIP', label: 'Installation workmanship', termYears: 5 },
] as const;

/**
 * How much of the contract must be in the bank before the project may leave the
 * advance stage.
 *
 * A percentage rather than an amount so it holds for a ₹2 lakh residential job
 * and a ₹2 crore industrial one. The check is overridable by a user holding
 * `projects:advance` with an explicit acknowledgement, because a client who has
 * paid 9% and signed for the rest is a commercial decision, not a data error.
 */
export const ADVANCE_PAYMENT_RULES = {
  minimumPctOfContract: 10,
} as const;

/**
 * Material types a plan is generated with from an approved system design.
 *
 * The quantity formula for each is in the material planning service; this is the
 * *shape* of a standard rooftop bill of materials, so a generated plan lists the
 * earthing kit even when nobody remembered to ask for one.
 */
export const MATERIAL_PLAN_TEMPLATE = [
  { productType: 'SOLAR_PANEL', label: 'Solar panels', uom: 'NOS' },
  { productType: 'INVERTER', label: 'Inverter', uom: 'NOS' },
  { productType: 'MOUNTING_STRUCTURE', label: 'Mounting structure', uom: 'KW' },
  { productType: 'CABLE', label: 'DC cable', uom: 'MTR' },
  { productType: 'CABLE', label: 'AC cable', uom: 'MTR' },
  { productType: 'CONNECTOR', label: 'MC4 connectors', uom: 'SET' },
  { productType: 'ACDB', label: 'ACDB', uom: 'NOS' },
  { productType: 'DCDB', label: 'DCDB', uom: 'NOS' },
  { productType: 'EARTHING_KIT', label: 'Earthing kit', uom: 'SET' },
  { productType: 'LIGHTNING_ARRESTER', label: 'Lightning arrester', uom: 'NOS' },
  { productType: 'ACCESSORY', label: 'Conduit and fasteners', uom: 'LOT' },
] as const;
